import type { DefaultComponentType } from '../../../../src/utils/types';
import '../input/Input.pcss';

export type TextAreaProps = DefaultComponentType & {
    id?: string;
    disabled?: boolean;
    name?: string;
    placeholder?: string;
    required?: boolean;
    autocomplete?: string;
    readonly?: boolean;
    minlength?: number;
    maxlength?: number;
    value?: string;
    rows?: number;
    cols?: number;
    onChange?: (event: Event) => void;
    onFocus?: (event: Event) => void;
    onBlur?: (event: Event) => void;
};

export default class TextArea extends HTMLTextAreaElement {
    #isDisabled: boolean = this.hasAttribute('aria-disabled');

    get isDisabled(): boolean {
        return this.#isDisabled;
    }

    set isDisabled(value: boolean) {
        this.#isDisabled = value;
        this.toggleAttribute('aria-disabled', value);
        this.toggleAttribute('readonly', value);
    }

    readonly #onFocusHandler: null | ((event: FocusEvent) => void) = null;

    readonly #onBlurHandler: null | ((event: Event) => void) = null;

    readonly #onChangeHandler: null | ((event: Event) => void) = null;

    constructor(props?: TextAreaProps) {
        super();

        if (props) {
            this.#onFocusHandler = props.onFocus || null;
            this.#onBlurHandler = props.onBlur || null;
            this.#onChangeHandler = props.onChange || null;
        }
    }

    public validate(): boolean {
        return this.validity.valid;
    }

    protected connectedCallback(): void {
        this.addEventListener('focus', this.#onFocus);
        this.addEventListener('change', this.#onChange);
        this.addEventListener('blur', this.#onBlur);
    }

    protected disconnectedCallback(): void {
        this.removeEventListener('focus', this.#onFocus);
        this.removeEventListener('change', this.#onChange);
        this.removeEventListener('blur', this.#onBlur);
    }

    #onFocus(event: FocusEvent): void {
        if (this.#isDisabled) {
            return;
        }

        if (this.#onFocusHandler) {
            this.#onFocusHandler(event);
        }
    }

    #onBlur(event: Event): void {
        if (this.#isDisabled) {
            return;
        }

        const valid = this.validate();
        if (!valid) {
            this.dispatchEvent(new Event('invalid'));
        }

        if (this.#onBlurHandler) {
            this.#onBlurHandler(event);
        }
    }

    #onChange(event: Event): void {
        if (this.#isDisabled) {
            return;
        }

        if (this.#onChangeHandler) {
            this.#onChangeHandler(event);
        }
    }
}

if (!window.customElements.get('nh-text-area')) {
    window.customElements.define('nh-text-area', TextArea, { extends: 'textarea' });
}
